import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSafeBack } from "../hooks/useSafeBack";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Shield, Loader2, CheckCircle2, XCircle } from "lucide-react";
import VerifiedBadgeChip from "../components/instructor/VerifiedBadgeChip";
import { MobileDialog } from "../components/mobile";

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdminBadgeQueue() {
  const nav = useNavigate();
  const goBack = useSafeBack();
  const [filter, setFilter] = useState("pending_review");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null); // badge object being decided
  const [expiry, setExpiry] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/instructor-badges/admin/queue?state=${filter}`, { withCredentials: true });
      setItems(data.items || []);
    } catch {
      toast.error("Couldn't load queue.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [filter]);

  const decide = async (decision) => {
    if (!active) return;
    if (decision === "approve" && !expiry) { toast.error("Pick an expiry date."); return; }
    if (decision === "reject" && !reason.trim()) { toast.error("Add a reason."); return; }
    setSaving(true);
    try {
      await axios.post(
        `${API}/api/instructor-badges/admin/decide/${active.id}`,
        { decision, expiry_date: expiry || null, reason: reason || null },
        { withCredentials: true }
      );
      toast.success(decision === "approve" ? "Approved." : "Marked needs update.");
      setActive(null); setExpiry(""); setReason("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't save decision.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-dvh no-overflow-x bg-slate-50 dark:bg-slate-950 pb-24">
      <header className="sticky top-0 z-10 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700">
        <div className="max-w-lg mx-auto px-4 py-3 flex items-center gap-3">
          <button data-testid="admin-badge-back" onClick={() => goBack()} className="p-2 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800"><ArrowLeft className="w-5 h-5 text-slate-600 dark:text-slate-400" /></button>
          <div className="flex-1 min-w-0">
            <h1 className="text-base font-extrabold text-slate-900 dark:text-white">Badge verification queue</h1>
            <p className="text-[11px] text-slate-500">Review instructor DVSA card submissions.</p>
          </div>
        </div>
      </header>

      <div className="max-w-lg mx-auto px-4 py-3">
        <div className="flex gap-1 mb-3">
          {[
            ["pending_review", "Pending"],
            ["verified_adi", "ADI"],
            ["verified_pdi", "PDI"],
            ["needs_update", "Needs update"],
            ["expired", "Expired"],
          ].map(([k, l]) => (
            <button
              key={k}
              data-testid={`filter-${k}`}
              onClick={() => setFilter(k)}
              className={`px-2.5 py-1.5 text-[11px] font-bold rounded-full border ${
                filter === k
                  ? "bg-indigo-600 text-white border-indigo-600"
                  : "bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-700"
              }`}
            >{l}</button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
        ) : items.length === 0 ? (
          <div data-testid="badge-queue-empty" className="rounded-2xl bg-slate-50 dark:bg-slate-800/50 border border-dashed border-slate-200 dark:border-slate-700 p-6 text-center">
            <Shield className="w-5 h-5 mx-auto text-slate-400 mb-1" />
            <p className="text-xs text-slate-500 font-bold">Nothing in this list</p>
          </div>
        ) : (
          <div className="space-y-2">
            {items.map((rec) => (
              <button
                key={rec.id}
                data-testid={`badge-row-${rec.id}`}
                onClick={() => { setActive(rec); setExpiry(rec.expiry_date || ""); setReason(rec.rejection_reason || ""); }}
                className="w-full text-left rounded-2xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-4 hover:border-indigo-400"
              >
                <div className="flex items-center justify-between mb-1">
                  <p className="text-sm font-extrabold text-slate-900 dark:text-white">{rec.instructor_name}</p>
                  <VerifiedBadgeChip verification={rec} />
                </div>
                <p className="text-[11px] text-slate-500">{rec.instructor_email} · {rec.badge_type?.toUpperCase()}{rec.badge_number ? ` · ${rec.badge_number}` : ""}</p>
                <p className="mt-1 text-[10px] text-slate-400">Submitted {rec.updated_at?.slice(0, 10)}</p>
              </button>
            ))}
          </div>
        )}
      </div>

      {active && (
        <MobileDialog
          bareContent
          open={!!active}
          onClose={() => setActive(null)}
          testid="badge-decision-modal-wrap"
        >
          <div data-testid="badge-decision-modal" className="w-full sm:max-w-md bg-white dark:bg-slate-900 rounded-t-3xl sm:rounded-3xl p-5 sm:p-6">
            <div className="mb-3">
              <p className="text-[11px] font-extrabold uppercase tracking-wider text-indigo-600">Review</p>
              <h2 className="mt-1 text-xl font-extrabold text-slate-900 dark:text-white">{active.instructor_name}</h2>
              <p className="text-[11px] text-slate-500">{active.badge_type?.toUpperCase()}{active.badge_number ? ` · ${active.badge_number}` : ""}</p>
            </div>
            <div className="grid grid-cols-2 gap-2 mb-3">
              <AdminPhoto url={active.front_url} label="Front" testid="admin-photo-front" />
              <AdminPhoto url={active.back_url}  label="Back"  testid="admin-photo-back" />
            </div>
            <label className="block mb-2">
              <span className="text-[10px] font-extrabold uppercase tracking-wider text-slate-500">Expiry (YYYY-MM-DD) — required on approve</span>
              <input data-testid="admin-expiry" type="date" value={expiry} onChange={(e) => setExpiry(e.target.value)}
                className="mt-1 w-full px-3 py-2 text-sm rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 dark:text-white" />
            </label>
            <label className="block mb-3">
              <span className="text-[10px] font-extrabold uppercase tracking-wider text-slate-500">Reason (shown to instructor if rejected)</span>
              <textarea data-testid="admin-reason" rows={2} value={reason} onChange={(e) => setReason(e.target.value)}
                className="mt-1 w-full px-3 py-2 text-sm rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 dark:text-white resize-none" />
            </label>
            <div className="grid grid-cols-2 gap-2">
              <button data-testid="admin-reject" onClick={() => decide("reject")} disabled={saving} className="px-3 py-2.5 text-xs font-extrabold rounded-xl bg-amber-600 text-white hover:bg-amber-500 disabled:opacity-60 flex items-center justify-center gap-1">
                <XCircle className="w-4 h-4" /> Needs update
              </button>
              <button data-testid="admin-approve" onClick={() => decide("approve")} disabled={saving} className="px-3 py-2.5 text-xs font-extrabold rounded-xl bg-emerald-600 text-white hover:bg-emerald-500 disabled:opacity-60 flex items-center justify-center gap-1">
                <CheckCircle2 className="w-4 h-4" /> Approve
              </button>
            </div>
            <button data-testid="admin-close" onClick={() => setActive(null)} className="mt-2 w-full px-3 py-2 text-[11px] font-bold rounded-xl bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300">Close</button>
          </div>
        </MobileDialog>
      )}
    </div>
  );
}

function AdminPhoto({ url, label, testid }) {
  if (!url) return <div className="aspect-[4/3] rounded-xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-[11px] text-slate-400">No {label.toLowerCase()}</div>;
  const API = process.env.REACT_APP_BACKEND_URL;
  return (
    <div data-testid={testid} className="rounded-xl overflow-hidden border border-slate-200 dark:border-slate-700">
      <img src={`${API}${url}`} alt={label} className="w-full aspect-[4/3] object-cover" />
      <p className="text-[10px] text-center py-1 text-slate-500 bg-slate-50 dark:bg-slate-800">{label}</p>
    </div>
  );
}
